// EquityValueAnchorStep — backsolve config + anchor adequacy (F2.1, F2.6, F6.4). Step 3.

import { useActiveValuation, useStore, useValidationIssues } from '../../store/selectors';
import { Field } from '../common/Field';
import { AnchorAdequacyBanner } from './AnchorAdequacyBanner';

export function EquityValueAnchorStep() {
  const active = useActiveValuation();
  const setEquityValue = useStore((s) => s.setEquityValue);
  const issues = useValidationIssues(active?.valuationId ?? null);
  if (!active) return null;
  const eq = active.inputs.equityValue;
  const preferred = active.inputs.capTableSnapshot.instruments.filter((i) => i.kind === 'preferred');

  const anchorIssues = issues.filter((i) =>
    ['ANCHOR_STALE', 'ANCHOR_NOT_ARMS_LENGTH', 'INVALID_ANCHOR_PRICE', 'ANCHOR_INSTRUMENT_MISSING'].includes(i.code),
  );

  return (
    <div className="card">
      <div className="card-title">Equity Value &amp; Anchor</div>
      <Field
        label="Source"
        htmlFor="source"
        helpText="Backsolve infers equity value from a recent preferred transaction. DCF, Market Multiples, and Tender enter a direct equity value directly."
      >
        <select
          id="source"
          className="select"
          value={eq.source}
          onChange={(e) => setEquityValue(active.valuationId, { source: e.target.value as typeof eq.source })}
        >
          <option value="backsolve">Backsolve (anchor transaction)</option>
          <option value="dcf">DCF</option>
          <option value="marketMultiples">Market Multiples</option>
          <option value="tender">Tender Offer</option>
        </select>
      </Field>
      {eq.source === 'backsolve' ? (
        <>
          <Field
            label="Anchor Instrument"
            htmlFor="anchorInst"
            helpText="Most recent preferred class used to back out the total equity value. The solver iterates V until the implied per-share value of this class matches the anchor price."
          >
            <select
              id="anchorInst"
              className="select"
              value={eq.anchorInstrumentId ?? ''}
              onChange={(e) =>
                setEquityValue(active.valuationId, { anchorInstrumentId: e.target.value || null })
              }
            >
              <option value="">(select)</option>
              {preferred.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (${p.issuePrice.toFixed(4)}/share)
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Anchor Price per Share (USD)"
            htmlFor="anchorPrice"
            helpText="Observed price per share of the anchor transaction in USD. Must be > 0 and arm's-length for the backsolve to be valid."
          >
            <input
              id="anchorPrice"
              className="input input-numeric"
              type="number"
              step="0.0001"
              value={eq.anchorPricePerShare ?? ''}
              onChange={(e) =>
                setEquityValue(active.valuationId, {
                  anchorPricePerShare: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          </Field>
        </>
      ) : (
        <Field
          label="Direct Equity Value (USD)"
          htmlFor="directEq"
          helpText="Total equity value in USD, entered directly from a DCF, multiples analysis, or tender-offer price. Used as the waterfall input V without running the solver."
        >
          <input
            id="directEq"
            className="input input-numeric"
            type="number"
            value={eq.directEquityValue ?? ''}
            onChange={(e) =>
              setEquityValue(active.valuationId, {
                directEquityValue: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
        </Field>
      )}
      <Field
        label="Transaction Date"
        htmlFor="txDate"
        helpText="ISO date of the anchor transaction or direct-value observation. Anchors older than 365 days trigger ANCHOR_STALE."
      >
        <input
          id="txDate"
          className="input"
          type="date"
          value={eq.transactionDate ?? ''}
          onChange={(e) =>
            setEquityValue(active.valuationId, { transactionDate: e.target.value || null })
          }
        />
      </Field>
      <Field
        label="Arm's-Length?"
        htmlFor="armLength"
        helpText="Confirm the anchor was transacted between independent parties. Unchecking raises ANCHOR_NOT_ARMS_LENGTH and weakens the safe-harbor position."
      >
        <label>
          <input
            id="armLength"
            type="checkbox"
            checked={eq.armLengthFlag}
            onChange={(e) => setEquityValue(active.valuationId, { armLengthFlag: e.target.checked })}
          />{' '}
          Anchor is arm&apos;s-length
        </label>
      </Field>
      {anchorIssues.length > 0 && <AnchorAdequacyBanner issues={anchorIssues} />}
    </div>
  );
}
